import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, Car, ChevronRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const SchademeldingenOverzicht = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/schademeldingen/management/overzicht`, { headers: hdrs() });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error('Laden mislukt');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totaalNieuw = data.reduce((s, r) => s + (r.nieuw || 0), 0);
  const totaalAll = data.reduce((s, r) => s + (r.totaal || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 sm:px-0" data-testid="schademeldingen-overzicht-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="schademeldingen-overzicht-title">
          <Car className="w-6 h-6" /> Schademeldingen Overzicht
        </h1>
        <p className="text-slate-500">Overzicht van alle locaties ({totaalAll} meldingen totaal)</p>
      </div>

      {/* Warning banner */}
      {totaalNieuw > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-800" data-testid="nieuw-warning">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{totaalNieuw} nieuwe schademelding{totaalNieuw !== 1 ? 'en' : ''} wachten op afhandeling</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto" data-testid="overzicht-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-4 font-semibold text-slate-700">Locatie</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Totaal</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Nieuw</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">In behandeling</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Afgehandeld</th>
                <th className="py-3 px-4 font-semibold text-slate-700">Laatste melding</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.map((row) => (
                <tr
                  key={row.locatie_naam}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/schademeldingen?locatie_id=${row.locatie_id}`)}
                  data-testid={`row-${row.locatie_naam}`}
                >
                  <td className="py-3 px-4 font-medium text-slate-700">{row.locatie_naam}</td>
                  <td className="py-3 px-4 text-center text-slate-600">{row.totaal}</td>
                  <td className="py-3 px-4 text-center">
                    {row.nieuw > 0 ? (
                      <Badge className="bg-red-100 text-red-700 text-xs">{row.nieuw}</Badge>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {row.in_behandeling > 0 ? (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">{row.in_behandeling}</Badge>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {row.afgehandeld > 0 ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">{row.afgehandeld}</Badge>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-600">{fmtDate(row.laatste_melding)}</td>
                  <td className="py-3 px-4"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400" data-testid="overzicht-empty">
                    Geen schademeldingen gevonden
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SchademeldingenOverzicht;
